import { useEffect, useMemo, useState } from "react";
import { Autocomplete, Fade } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectActiveRobotVersion } from "@/store/ses/sesRobotSlice";
import { changeConnectorName, changeNumberValuesMessengerConnector, changeStringValuesMessengerConnector } from "@/store/ses/sesConnectorSlice";
import { selectDataServers } from "@/store/serverSlice";
import { useAccessRight, useTranslate } from "@/hooks";
import { SES } from "@/constants/accessRights";
import Activator from "../Activator/Activator";
import FieldValueBlock from "../FieldValueBlock/FieldValueBlock";
import { Hint } from "@/HOC";
import { RenderOption, CustomTextField, H3 } from "@/ui";
import { IMessengerProps } from "./Messenger.props";
import styles from "./Messenger.module.scss";

const Messenger = ({ selectId, data: { channel, settings }, connectorName, changeFlgHandler }: IMessengerProps): JSX.Element => {
	const [inputConnectorName, setInputConnectorName] = useState<string>(""); // название коннектора
	const [inputBotName, setInputBotName] = useState<string>(""); // название бота
	const [inputGroup, setInputGroup] = useState<string>(""); // группа
	const [inputResetPhrase, setInputResetPhrase] = useState<string>(""); // фраза сброса сессии
	const [inputUpdateTimer, setInputUpdateTimer] = useState<number>(0); // таймер обновления новых сообщений
	const [inputToken, setInputToken] = useState<string>(""); // токен
	const [inputServer, setInputServer] = useState<string>(""); // обслуживающий сервер

	const dispatch = useAppDispatch();
	const activeRobotVersion = useAppSelector(selectActiveRobotVersion); // store - версия активного робота
	const dataServers = useAppSelector(selectDataServers); // store - данные о серверах

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	const isDisabled = useMemo(() => {
		return !isAccess(SES.CONNECTOR_MODIFY) || activeRobotVersion === "backup";
	}, [activeRobotVersion]);

	// следим за каналом, названием коннектора и заполняем данные
	useEffect(() => {
		setInputConnectorName(connectorName);
		setInputBotName(settings.botName);
		setInputToken(settings.token);
		setInputServer(settings.server);
		setInputUpdateTimer(settings.pollingInterval);
		channel === "vk" && setInputGroup("groupId" in settings ? settings.groupId : "");
		setInputResetPhrase("reset" in settings && settings.reset ? settings.reset : "");
	}, [selectId, channel, connectorName, settings]);

	// обработчик изменения названия коннектора
	const connectorNameChangeHandler = (): void => {
		if (connectorName === inputConnectorName) return;
		dispatch(changeConnectorName({ newName: inputConnectorName }));
		changeFlgHandler("name");
	};

	// обработчик изменения названия бота
	const botNameChangeHandler = (): void => {
		if (settings.botName === inputBotName) return;
		dispatch(changeStringValuesMessengerConnector({ channel, field: "botName", value: inputBotName }));
		changeFlgHandler("params");
	};

	// обработчик изменения группы
	const groupChangeHandler = (): void => {
		if (channel === "vk" && "groupId" in settings && settings.groupId === inputGroup) return;
		dispatch(changeStringValuesMessengerConnector({ channel, field: "groupId", value: inputGroup }));
		changeFlgHandler("params");
	};

	// обработчик изменения фразы сброса сессии
	const resetPhraseChangeHandler = (): void => {
		if ("reset" in settings && settings.reset && settings.reset === inputResetPhrase) return;
		dispatch(changeStringValuesMessengerConnector({ channel, field: "reset", value: inputResetPhrase }));
		changeFlgHandler("params");
	};

	// обработчик изменения таймера обновления
	const updateTimerChangeHandler = (): void => {
		if (settings.pollingInterval === inputUpdateTimer) return;
		dispatch(changeNumberValuesMessengerConnector({ channel, field: "pollingInterval", value: inputUpdateTimer }));
		changeFlgHandler("params");
	};

	// обработчик изменения токена
	const tokenChangeHandler = (): void => {
		if (settings.token === inputToken) return;
		dispatch(changeStringValuesMessengerConnector({ channel, field: "token", value: inputToken }));
		changeFlgHandler("params");
	};

	// обработчик изменения сервера
	const serverChangeHandler = (): void => {
		if (settings.server === inputServer) return;
		dispatch(changeStringValuesMessengerConnector({ channel, field: "server", value: inputServer }));
		changeFlgHandler("params");
	};

	return (
		<Fade in={true} timeout={500}>
			<div className={styles.container}>
				{/* общие настройки */}
				<H3 className={styles.header}>{translate("title_generalSettings")}</H3>
				<div className={styles.containerInner}>
					<div className={styles.block}>
						{/* название коннектора */}
						<CustomTextField
							fullWidth
							required
							label={translate("input_connectorName")}
							value={inputConnectorName}
							onChange={(e) => setInputConnectorName(e.target.value)}
							onBlur={connectorNameChangeHandler}
							error={!inputConnectorName}
							disabled={isDisabled}
							hint={"hint_connectorsTitle"}
							hintPosition="left"
						/>

						{/* группа */}
						{channel === "vk" && (
							<CustomTextField
								fullWidth
								required
								label={translate("input_group")}
								value={inputGroup}
								onChange={(e) => setInputGroup(e.target.value)}
								onBlur={groupChangeHandler}
								error={!inputGroup}
								disabled={isDisabled}
								hint={"hint_connectorsVkGroup"}
							/>
						)}

						{/* фраза сброса сессии */}
						<CustomTextField
							label={translate("input_sessionResetPhrase")}
							fullWidth
							value={inputResetPhrase}
							onChange={(e) => setInputResetPhrase(e.target.value)}
							onBlur={resetPhraseChangeHandler}
							disabled={isDisabled}
							hint={"hint_connectorsResetPhrase"}
						/>
					</div>

					{/* токен */}
					<div className={styles.block}>
						<CustomTextField
							fullWidth
							required
							label={translate("input_token")}
							value={inputToken}
							onChange={(e) => setInputToken(e.target.value)}
							onBlur={tokenChangeHandler}
							error={!inputToken}
							disabled={isDisabled}
							hint={"hint_connectorsToken"}
						/>
					</div>

					<div className={styles.block}>
						{/* название бота */}
						<CustomTextField
							required
							label={translate("input_botName")}
							value={inputBotName}
							fullWidth
							onChange={(e) => setInputBotName(e.target.value)}
							onBlur={botNameChangeHandler}
							error={!inputBotName}
							disabled={isDisabled}
							hint={"hint_connectorsBotName"}
						/>
						{/* сервер */}
						<Autocomplete
							fullWidth
							freeSolo
							options={Object.values(dataServers?.names || {})}
							value={inputServer}
							onChange={(_, value) => setInputServer(value ? value : "")}
							onBlur={serverChangeHandler}
							disabled={isDisabled}
							renderInput={(params) => (
								<CustomTextField
									{...params}
									required
									label={translate("input_servingServer")}
									onChange={(e) => setInputServer(e.target.value)}
									error={!inputServer}
									hint={"hint_connectorsServingServer"}
								/>
							)}
							renderOption={(props, option) => {
								return (
									<RenderOption {...props} key={option}>
										{option}
									</RenderOption>
								);
							}}
						/>
						{/* таймер обновления */}
						<CustomTextField
							fullWidth
							required
							type="number"
							label={translate("input_updateFrequency")}
							value={inputUpdateTimer}
							onChange={(e) => setInputUpdateTimer(+e.target.value)}
							onBlur={updateTimerChangeHandler}
							error={inputUpdateTimer < 1}
							disabled={isDisabled}
							InputProps={{
								inputProps: { min: 1 },
							}}
							hint={"hint_connectorsUpdateFrequency"}
						/>
					</div>
				</div>

				{/* фразы-активаторы */}
				<H3 className={styles.header}>{translate("title_activatorPhrases")}</H3>
				<div className={styles.containerInner}>
					{settings.activators.concat("").map((phrase, idx, array) => (
						<div className={styles.containerInnerField} key={phrase + idx}>
							<Hint hint={"hint_connectorsActivatorPhrases"} width="100%">
								<Activator channel={channel} phrase={phrase} idx={idx} array={array} changeFlgHandler={changeFlgHandler} isDisabled={isDisabled} />
							</Hint>
						</div>
					))}
				</div>

				{/* переменные */}
				<H3 className={styles.header}>{translate("title_variables")}</H3>
				{Object.entries(settings.variables)
					.concat([["", ""]])
					.map(([variable, value], idx, array) => (
						<Hint hint={"hint_connectorsVariable"} width="100%" key={idx}>
							<FieldValueBlock
								channel={channel}
								setting="variables"
								field={variable}
								fieldLabel="input_variable"
								value={value}
								valueLabel="input_value"
								idx={idx}
								array={array}
								changeFlgHandler={changeFlgHandler}
								isDisabled={isDisabled}
							/>
						</Hint>
					))}

				{/* доступы */}
				<H3 className={styles.header}>{translate("title_accesses")}</H3>
				{Object.entries(settings.whitelist)
					.concat([["", ""]])
					.map(([id, desc], idx, array) => (
						<Hint hint={"hint_connectorsUserId"} width="100%" key={idx}>
							<FieldValueBlock
								key={idx}
								channel={channel}
								setting="whitelist"
								field={id}
								fieldLabel="input_userId"
								value={desc}
								valueLabel="input_comment"
								idx={idx}
								array={array}
								changeFlgHandler={changeFlgHandler}
								isDisabled={isDisabled}
							/>
						</Hint>
					))}
			</div>
		</Fade>
	);
};

export default Messenger;
